import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Button,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { FiUserPlus, FiTrash2, FiEdit2 } from "react-icons/fi";
import axios from "axios";
// ✅ Import your auth context
import { useAuth } from "../../contexts/AuthContext"; // Adjust path as needed

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const ProjectStaffManagement = ({ projectId, projectName }) => {
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedRole, setSelectedRole] = useState("viewer");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const toast = useToast();
  
  // ✅ Get token from auth context
  const { token, user } = useAuth();

  const {
    isOpen: isAssignOpen,
    onOpen: onAssignOpen,
    onClose: onAssignClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const cancelRef = React.useRef();

  const projectRoles = [
    { value: "project_manager", label: "Project Manager", color: "purple" },
    { value: "site_engineer", label: "Site Engineer", color: "green" },
    { value: "supervisor", label: "Supervisor", color: "blue" },
    { value: "viewer", label: "Viewer", color: "gray" },
  ];

  useEffect(() => {
    if (projectId && token) {
      fetchStaffList();
    }
  }, [projectId, token]);

  // ✅ UPDATED: Use token from context
  const getAuthHeader = () => {
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStaffList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/project-staff/project/${projectId}`,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        setStaffList(response.data.data.assignments);
      }
    } catch (error) {
      console.error("Error fetching staff list:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to fetch staff list",
        status: "error",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/project-staff/available`, {
        headers: getAuthHeader(),
        params: { projectId },
      });

      if (response.data.success) {
        setAvailableStaff(response.data.data.users);
      }
    } catch (error) {
      console.error("Error fetching available staff:", error);
      toast({
        title: "Error",
        description: "Failed to fetch available staff",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedUser || !selectedRole) {
      toast({
        title: "Validation Error",
        description: "Please select both user and role",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/project-staff/assign`,
        {
          projectId,
          userId: selectedUser,
          projectRole: selectedRole,
          permissions: {},
        },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Staff member assigned successfully",
          status: "success",
          duration: 3000,
        });
        fetchStaffList();
        onAssignClose();
        setSelectedUser("");
        setSelectedRole("viewer");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to assign staff member",
        status: "error",
        duration: 4000,
      });
    }
  };

  const handleRemoveStaff = async () => {
    if (!deleteTarget) return;

    try {
      const response = await axios.delete(
        `${API_URL}/project-staff/${deleteTarget.id}`,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Staff member removed successfully",
          status: "success",
          duration: 3000,
        });
        fetchStaffList();
        onDeleteClose();
        setDeleteTarget(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove staff member",
        status: "error",
        duration: 4000,
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    const roleData = projectRoles.find((r) => r.value === role);
    return roleData?.color || "gray";
  };

  const getRoleLabel = (role) => {
    const roleData = projectRoles.find((r) => r.value === role);
    return roleData?.label || role;
  };

  const openAssignModal = () => {
    fetchAvailableStaff();
    onAssignOpen();
  };

  // ✅ Show error if no token
  if (!token) {
    return (
      <Alert status="error">
        <AlertIcon />
        Authentication token not found. Please log in again.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <Card bg="gray.800" borderColor="gray.700">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="md" color="white">
                Project Staff ({staffList.length})
              </Heading>
              <Text color="gray.400" fontSize="sm">
                Manage staff assignments and roles for this project
              </Text>
            </VStack>
            <Button
              leftIcon={<FiUserPlus />}
              colorScheme="blue"
              onClick={openAssignModal}
            >
              Assign Staff
            </Button>
          </Flex>
        </CardHeader>

        <CardBody>
          {staffList.length === 0 ? (
            <Alert status="info" bg="gray.700" color="white">
              <AlertIcon />
              No staff members assigned to this project yet.
            </Alert>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color="gray.400">Name</Th>
                  <Th color="gray.400">Email</Th>
                  <Th color="gray.400">Role</Th>
                  <Th color="gray.400">Assigned Date</Th>
                  <Th color="gray.400">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {staffList.map((assignment) => (
                  <Tr key={assignment.id}>
                    <Td color="white">
                      {assignment.user.firstName} {assignment.user.lastName}
                    </Td>
                    <Td color="gray.300">{assignment.user.email}</Td>
                    <Td>
                      <Badge
                        colorScheme={getRoleBadgeColor(assignment.projectRole)}
                      >
                        {getRoleLabel(assignment.projectRole)}
                      </Badge>
                    </Td>
                    <Td color="gray.300">
                      {new Date(assignment.assignedAt).toLocaleDateString()}
                    </Td>
                    <Td>
                      <IconButton
                        icon={<FiTrash2 />}
                        colorScheme="red"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDeleteTarget(assignment);
                          onDeleteOpen();
                        }}
                        aria-label="Remove staff"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Assign Staff Modal */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader color="white">Assign Staff to Project</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color="white">Select Staff Member</FormLabel>
                <Select
                  placeholder="Choose a staff member"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  bg="gray.700"
                  color="white"
                >
                  {availableStaff.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color="white">Project Role</FormLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  bg="gray.700"
                  color="white"
                >
                  {projectRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Alert status="info" bg="gray.700" color="white" fontSize="sm">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold" mb={1}>
                    Role Descriptions:
                  </Text>
                  <VStack align="start" spacing={1} fontSize="xs">
                    <Text>
                      • <strong>Project Manager:</strong> Full project access
                    </Text>
                    <Text>
                      • <strong>Site Engineer:</strong> Edit progress & reports
                    </Text>
                    <Text>
                      • <strong>Supervisor:</strong> Update progress data
                    </Text>
                    <Text>
                      • <strong>Viewer:</strong> Read-only access
                    </Text>
                  </VStack>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAssignStaff}
              isDisabled={!selectedUser || !selectedRole}
            >
              Assign Staff
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">
              Remove Staff Member
            </AlertDialogHeader>

            <AlertDialogBody color="white">
              Are you sure you want to remove{" "}
              <strong>
                {deleteTarget?.user.firstName} {deleteTarget?.user.lastName}
              </strong>{" "}
              from this project?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleRemoveStaff} ml={3}>
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ProjectStaffManagement;
